import {
  Claim,
  CreateClaimForm as CreateClaimFormValues,
  Payor,
  Provider,
  SharedClaimForm,
  Site,
  Status,
} from 'interfaces';
import { CLAIM_STATUS } from '@billing/validation/baseClaim';
import { MRT_Row, MRT_TableInstance } from 'mantine-react-table';
import { useEffect, useMemo, useRef } from 'react';
import { useLiveClaimRules } from 'hooks/useLiveClaimRules';
import { useLiveBulkClaimRules } from 'hooks/useLiveBulkClaimRules';
import {
  useCurrentUserStore,
  useSelectedClaimStore,
  useClaimDraftStore,
  resolveSiteDefaultPayor,
} from 'store';
import { getPersistedBulkDraft } from 'store/ClaimDraftStore';
import { mapClaimToCreateClaimForm } from 'utils/duplicateClaimUtils';
import { ClaimFormProvider } from './ClaimFormProvider';
import {
  BulkCreateClaimModal,
  BulkForm,
  ClaimCreateModal,
  CreateClaimForm,
} from './ModalComponents';

interface ClaimCreateRowModalContentProps {
  table: MRT_TableInstance<Claim>;
  row: MRT_Row<Claim>;
  internalEditComponents: React.ReactNode[];
  status: Status[];
  site: Site;
  payors: Payor[];
  providers: Provider[];
  createModalType: 'claim' | 'bulk' | 'repeat';
  useSelectedClaimAsInitialValues?: boolean;
  quickCreatePrefill?: {
    createInitialValues?: Partial<CreateClaimFormValues>;
    bulkSharedInitialValues?: Partial<SharedClaimForm>;
  };
}

export const ClaimCreateRowModalContent = ({
  table,
  row,
  internalEditComponents,
  site,
  payors,
  providers,
  createModalType,
  useSelectedClaimAsInitialValues = false,
  quickCreatePrefill,
}: ClaimCreateRowModalContentProps) => {
  // one time snapshot of draft state when modal is opened
  // to determine if we should restore draft data into form
  // saving a draft while the modal is open won't affect these values
  const bulkDraft = useClaimDraftStore.getState().bulk;
  const createDraft = useClaimDraftStore.getState().create;
  const { selectedClaim } = useSelectedClaimStore();
  const { id: userID } = useCurrentUserStore();
  const inProgressStatus = CLAIM_STATUS.IN_PROGRESS;
  const defaultBulkPayor = resolveSiteDefaultPayor(site, payors);

  const duplicateInitialValues = useMemo(() => {
    if (!useSelectedClaimAsInitialValues || !selectedClaim) {
      return null;
    }
    return mapClaimToCreateClaimForm(
      selectedClaim,
      {
        status: inProgressStatus,
        siteID: site.id,
        site,
      },
      userID
    );
  }, [
    selectedClaim,
    inProgressStatus,
    site,
    useSelectedClaimAsInitialValues,
    userID,
  ]);

  const initialBulkDraftRef = useRef(getPersistedBulkDraft() ?? bulkDraft);
  const initialCreateDraftRef = useRef(createDraft?.data);
  const quickCreateInitialValues = quickCreatePrefill?.createInitialValues;
  const liveRules = useLiveClaimRules({});
  const liveBulkRules = useLiveBulkClaimRules({ siteID: site.id });
  const usesLiveClaimRules = createModalType === 'claim';
  const didInitializeLiveRulesRef = useRef(false);

  const createForm = CreateClaimForm({
    status: inProgressStatus,
    row,
    site,
    payors: payors,
    initialValues:
      duplicateInitialValues ?? quickCreateInitialValues ?? undefined,
    onValuesChange: usesLiveClaimRules
      ? (values) => liveRules.scheduleEvaluation(values)
      : undefined,
  });
  if (usesLiveClaimRules && !didInitializeLiveRulesRef.current) {
    liveRules.setEvaluationBaseline(createForm.getValues());
    didInitializeLiveRulesRef.current = true;
  }

  const bulkForm = BulkForm({
    status: inProgressStatus,
    site,
    payor: defaultBulkPayor,
    initialSharedValues:
      quickCreatePrefill?.bulkSharedInitialValues ??
      initialBulkDraftRef.current?.shared,
    onValuesChange: (values) => liveBulkRules.scheduleEvaluation(values.claims),
  });
  const isBulkMode = createModalType === 'bulk';
  const formType = isBulkMode ? 'bulk' : 'create';

  // determine if we opened with a draft
  // to restore into form on first render
  // (only if we haven't already restored)
  const openedWithBulkDraft =
    initialBulkDraftRef.current &&
    Array.isArray(initialBulkDraftRef.current.data) &&
    initialBulkDraftRef.current.data.length > 0 &&
    !quickCreatePrefill?.bulkSharedInitialValues;

  const openedWithCreateDraft =
    !!initialCreateDraftRef.current &&
    !duplicateInitialValues &&
    !quickCreateInitialValues;
  const didRestoreRef = useRef(false);

  useEffect(() => {
    if (didRestoreRef.current) return;

    if (duplicateInitialValues) {
      didRestoreRef.current = true;
      return;
    }

    if (formType === 'create' && openedWithCreateDraft) {
      liveRules.setEvaluationBaseline(initialCreateDraftRef.current);
      createForm.setValues(initialCreateDraftRef.current);
      didRestoreRef.current = true;
      return;
    }

    if (formType === 'bulk' && openedWithBulkDraft) {
      const draft = initialBulkDraftRef.current;
      const prev = bulkForm.getValues();
      bulkForm.setValues({
        ...prev,
        shared: { ...prev.shared, ...draft.shared },
        claims: draft.data,
      });
      didRestoreRef.current = true;
    }
  }, [
    bulkForm,
    createForm,
    duplicateInitialValues,
    formType,
    liveRules.setEvaluationBaseline,
    openedWithBulkDraft,
    openedWithCreateDraft,
  ]);

  return (
    <ClaimFormProvider
      form={{
        formType: formType,
        editForm: undefined,
        createForm: !isBulkMode ? createForm : undefined,
        bulkForm: isBulkMode ? bulkForm : undefined,
        prefix: !isBulkMode ? '' : 'shared',
      }}
    >
      {!isBulkMode ? (
        <ClaimCreateModal
          table={table}
          row={row}
          internalEditComponents={internalEditComponents}
          providers={providers}
          payors={payors}
          mode={createModalType === 'repeat' ? 'repeat' : 'single'}
          liveRules={usesLiveClaimRules ? liveRules : undefined}
          duplicateSourceClaim={
            useSelectedClaimAsInitialValues ? selectedClaim : null
          }
        />
      ) : (
        <BulkCreateClaimModal
          table={table}
          row={row}
          internalEditComponents={internalEditComponents}
          providers={providers}
          payors={payors}
          liveRules={liveBulkRules}
        />
      )}
    </ClaimFormProvider>
  );
};
